/**
 * MembershipSnapshotCard — Admin Diagnostics super-admin panel (Feb 2026).
 * Read-only view of every source of truth for a project's roster:
 *   - projects.team[]
 *   - contractors.project_assignments
 *   - project_memberships (canonical)
 *   - RFP recipients
 *   - portal identity rows
 * Renders raw JSON with per-source counts. Proof-of-state tool for real-use
 * verification without terminal commands.
 */
import { useState } from 'react';
import axios from 'axios';
import { RefreshCw, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MembershipSnapshotCard() {
  const [projectId, setProjectId] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});

  const run = async () => {
    if (!projectId.trim()) return setError('Enter a project id');
    setError(''); setLoading(true); setSnapshot(null);
    try {
      const r = await axios.get(`${API}/admin/membership-snapshot`, {
        params: { project_id: projectId.trim() },
      });
      setSnapshot(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to fetch snapshot');
    } finally { setLoading(false); }
  };

  const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

  return (
    <Card data-testid="membership-snapshot-card">
      <CardHeader>
        <CardTitle>Membership Snapshot (super-admin)</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-3">
          Reads the LIVE documents for a project across every source of truth. Read-only.
        </p>
        <div className="flex gap-2 mb-2">
          <Input
            placeholder="Project id (UUID)"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="font-mono text-xs"
            data-testid="membership-snapshot-project-id-input"
          />
          <Button
            onClick={run} disabled={loading}
            className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
            data-testid="membership-snapshot-run-btn"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Snapshot'}
          </Button>
        </div>
        {/* v7.2 (2026-07-12) — DEFECT 4 FIX. The card is a free-text
            input, not a picker; a user reported "the picker excludes
            the fixture project". Ensure the fixture id is one click
            away — the snapshot backend has no is_test_fixture filter,
            so this ID works verbatim. */}
        <div className="mb-3">
          <Button
            variant="outline"
            size="sm"
            className="text-xs font-mono"
            onClick={() => setProjectId('7a0a0894-6b40-4882-82c2-e7905b2c387d')}
            data-testid="membership-snapshot-fill-fixture"
          >
            Fill fixture project id
          </Button>
        </div>
        {error && (
          <div className="text-xs text-red-700 bg-red-50 rounded p-2 mb-3" data-testid="membership-snapshot-error">
            {error}
          </div>
        )}
        {snapshot && (
          <div className="space-y-3" data-testid="membership-snapshot-result">
            <div className="text-xs">
              <div><b>Project:</b> {snapshot.project?.name || '(no name)'} <span className="text-muted-foreground">({snapshot.project_id})</span></div>
              <div><b>Company:</b> <span className="font-mono">{snapshot.project?.company_id}</span></div>
              <div><b>Is fixture:</b> {String(snapshot.project?.is_test_fixture || false)}</div>
              <div><b>Generated:</b> {snapshot.generated_at}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              {Object.entries(snapshot.counts).map(([k, v]) => (
                <Badge key={k} variant="outline" className="justify-between">
                  <span>{k}</span><span className="font-mono ml-2">{v}</span>
                </Badge>
              ))}
            </div>
            {Object.entries(snapshot.sources).map(([k, rows]) => (
              <div key={k} className="border rounded" data-testid={`membership-snapshot-source-${k}`}>
                <button
                  onClick={() => toggle(k)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 text-left"
                >
                  <div className="flex items-center gap-2">
                    {open[k] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <span className="font-medium text-sm">{k}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{rows.length}</Badge>
                </button>
                {open[k] && (
                  <pre className="text-[10px] p-3 bg-gray-50 overflow-auto max-h-96 font-mono border-t">
                    {rows.length === 0 ? '(empty)' : JSON.stringify(rows, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
